import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Link,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import Layout from "../../components/layout/Layout";
import Panel from "../../components/brand/Panel";
import KpiTile from "../../components/brand/KpiTile";
import { fonts } from "../../theme/tokens";
import { getFinanceDashboard } from "../../api/financeApi";
import { searchProperties } from "../../api/propertyApi";
import { formatDateUk } from "../../utils/dateDisplay";
import PropertyFinanceWorkspace from "./PropertyFinanceWorkspace";

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

export default function FinanceDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const propertyId = searchParams.get("propertyId") || "";
  const workspaceRef = useRef(null);
  const searchInputRef = useRef(null);
  const barRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [debtorsPage, setDebtorsPage] = useState(1);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertySearching, setPropertySearching] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getFinanceDashboard(debtorsPage);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load finance dashboard.");
    } finally {
      setLoading(false);
    }
  }, [debtorsPage]);

  useEffect(() => {
    load();
  }, [load]);

  const runPropertySearch = useCallback(async (query) => {
    try {
      setPropertySearching(true);
      const results = await searchProperties(query, 20, true);
      setPropertyOptions(Array.isArray(results) ? results : []);
    } catch {
      setPropertyOptions([]);
    } finally {
      setPropertySearching(false);
    }
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      runPropertySearch("");
    }, 0);
    return () => clearTimeout(handle);
  }, [runPropertySearch]);

  const selectProperty = (id, tab, option) => {
    const next = new URLSearchParams(searchParams);
    next.set("propertyId", String(id));
    if (!tab || tab === "overview") next.delete("tab");
    else next.set("tab", tab);
    if (option?.budget_id) next.set("budgetId", String(option.budget_id));
    else next.delete("budgetId");
    setSearchParams(next);
    if (option) {
      setSelectedProperty({
        property_id: option.property_id || option.id,
        property_name: option.property_name || option.name,
        address_line1: option.address_line1,
        city: option.city,
        postcode: option.postcode,
      });
    }
    requestAnimationFrame(() => {
      barRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const clearProperty = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("propertyId");
    next.delete("tab");
    next.delete("budgetId");
    setSearchParams(next);
    setSelectedProperty(null);
  };

  const propertyAddress = (option) =>
    [option?.address_line1, option?.city, option?.postcode].filter(Boolean).join(", ");

  const metrics = data?.metrics || {};
  const debtors = data?.debtors || { results: [], total_pages: 1, total: 0 };

  const workItems = useMemo(() => {
    const renewalIds = new Set((data?.renewals_due || []).map((row) => row.id));
    const items = [];
    (data?.renewals_due || []).forEach((row) => {
      items.push({
        key: `budget-${row.id}`,
        property_id: row.property_id,
        property_name: row.property_name,
        budget_id: row.id,
        tab: "budgets",
        why: "Renewal due",
        detail: `${row.name} · ${row.days_to_renewal} days`,
      });
    });
    (data?.pending_approval || []).forEach((row) => {
      if (renewalIds.has(row.id)) return;
      items.push({
        key: `budget-${row.id}`,
        property_id: row.property_id,
        property_name: row.property_name,
        budget_id: row.id,
        tab: "budgets",
        why: "Waiting for approval",
        detail: `${row.name} · year ends ${formatDateUk(row.fiscal_year_end)}`,
      });
    });
    (data?.recalculations_needed || []).forEach((row) => {
      items.push({
        key: `recalc-${row.property_id}`,
        property_id: row.property_id,
        property_name: row.property_name,
        tab: "overview",
        why: "Recalculation needed",
        detail: row.reason,
      });
    });
    return items;
  }, [data]);

  const propertyName = selectedProperty?.property_name || "Property";

  return (
    <Layout>
      <Box
        ref={barRef}
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", letterSpacing: 1.2, fontFamily: fonts.mono }}
          >
            Finance
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
            {propertyId ? propertyName : "Finance dashboard"}
          </Typography>
        </Box>
        {propertyId && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={clearProperty}>
              All properties
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => workspaceRef.current?.openCreateWizard()}
            >
              Create budget
            </Button>
            <Link
              component={RouterLink}
              to={`/properties/${propertyId}/details`}
              sx={{ whiteSpace: "nowrap" }}
            >
              Open property record
            </Link>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: { xs: "stretch", sm: "flex-start" },
          flexDirection: { xs: "column", sm: "row" },
          mb: 3,
        }}
      >
        <Autocomplete
          sx={{ flex: 1 }}
          options={propertyOptions}
          loading={propertySearching}
          value={propertyId ? selectedProperty : null}
          onChange={(_, value) => {
            if (!value) clearProperty();
            else selectProperty(value.property_id, "overview", value);
          }}
          filterOptions={(options) => options}
          onInputChange={(_, value, reason) => {
            if (reason !== "input") return;
            clearTimeout(searchInputRef.debounce);
            searchInputRef.debounce = setTimeout(() => runPropertySearch(value), 300);
          }}
          onOpen={() => {
            if (propertyOptions.length === 0) runPropertySearch("");
          }}
          getOptionLabel={(option) => option.property_name || ""}
          isOptionEqualToValue={(option, value) => option.property_id === value.property_id}
          renderOption={(props, option) => (
            <li {...props} key={option.property_id}>
              <Box>
                <Typography variant="body2">{option.property_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {propertyAddress(option) || "No address"}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={searchInputRef}
              label={propertyId ? "Switch property" : "Work on a property"}
              placeholder="Search by name or address"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {propertySearching ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!propertyId && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          {loading ? (
            [...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={100} />)
          ) : (
            <>
              <KpiTile label="Budget total (YTD)" value={money(metrics.budget_total)} />
              <KpiTile label="Collected (YTD)" value={money(metrics.collected_ytd)} />
              <KpiTile label="Spent (YTD)" value={money(metrics.spent_ytd)} />
              <KpiTile label="Outstanding" value={money(metrics.outstanding_ytd)} />
            </>
          )}
        </Box>
      )}

      {propertyId && (
        <PropertyFinanceWorkspace
          ref={workspaceRef}
          propertyId={propertyId}
          includeSpendTabs
          onPropertyLoaded={(property) => {
            setSelectedProperty({
              property_id: property.id,
              property_name: property.name,
              address_line1: property.address_line1,
              city: property.city,
              postcode: property.postcode,
            });
          }}
        />
      )}

      {!propertyId && (
        <>
      <Panel title="Work to do" noBodyPadding={false} sx={{ mb: 3 }}>
        {loading ? (
          <Skeleton height={120} />
        ) : workItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing needs attention.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Property</TableCell>
                  <TableCell>Why</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workItems.map((row) => (
                  <TableRow key={row.key} hover>
                    <TableCell>
                      <Button
                        startIcon={<ChevronRightIcon />}
                        onClick={() =>
                          selectProperty(row.property_id, row.tab, {
                            property_id: row.property_id,
                            property_name: row.property_name,
                            budget_id: row.budget_id,
                          })
                        }
                        sx={{ p: 0, minWidth: 0, textTransform: "none", fontWeight: 500 }}
                      >
                        {row.property_name}
                      </Button>
                    </TableCell>
                    <TableCell>{row.why}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-line" }}>{row.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Panel>

      <Panel
        title="Debtors (3+ months arrears)"
        noBodyPadding={false}
        right={
          <Link
            component={RouterLink}
            to="/properties"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            <AccountBalanceWalletIcon fontSize="small" />
            View properties
          </Link>
        }
      >
        {loading ? (
          <Skeleton height={200} />
        ) : debtors.results.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No units in arrears over three months.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell align="right">Days overdue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {debtors.results.map((row) => (
                    <TableRow key={`${row.property_id}-${row.unit_id}`} hover>
                      <TableCell>{row.property_name}</TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/units/${row.unit_id}/statement`}
                        >
                          {row.unit_name}
                        </Link>
                      </TableCell>
                      <TableCell>{row.owner_name || "—"}</TableCell>
                      <TableCell align="right">{money(row.outstanding)}</TableCell>
                      <TableCell align="right">{row.days_overdue}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {debtors.total_pages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={debtors.total_pages}
                  page={debtorsPage}
                  onChange={(_, p) => setDebtorsPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Panel>
        </>
      )}
    </Layout>
  );
}
